import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import DashboardLayout from '../../DashboardLayout';
import { CCPOPRPSievesProvider, useCCPOPRPSievesContext, CCP_SCREENS } from './context/CCPOPRPSievesContext';
import type { CompletedCycleRecord } from './context/CCPOPRPSievesContext';
import { ccpOprpSievesService } from './services/ccpOprpSievesService';
import ChecklistInformation from './components/ChecklistInformation';
import CCPOPRPChecklist from './components/CCPOPRPChecklist';
import SievesMagnetsChecklist from './components/SievesMagnetsChecklist';
import SummaryReporting from './components/SummaryReporting';
import './styles/ccpOprpSieves.css';
import FullPageLoader from '../../common/FullPageLoader';

const CCP_OPRP_SievesMain: React.FC = () => {
    const { state, dispatch } = useCCPOPRPSievesContext();
    const [searchParams] = useSearchParams();
    const resumedTourIdRef = useRef<string | null>(null);

    const tourIdParam = searchParams.get('id') || searchParams.get('TourId') || searchParams.get('tourId');

    useEffect(() => {
        if (!tourIdParam || resumedTourIdRef.current === tourIdParam) return;
        resumedTourIdRef.current = tourIdParam;

        const resumeTour = async () => {
            dispatch({ type: 'SET_LOADING', payload: true });
            try {
                const cleanTourId = tourIdParam.replace(/[{}]/g, "").trim().toLowerCase();
                const tours = await ccpOprpSievesService.getTourHistory();
                const tour = (tours || []).find((t: any) => {
                    const id1 = String(t.cr3ea_prod_rajpura_quality_tourid || "").replace(/[{}]/g, "").trim().toLowerCase();
                    const id2 = String(t.cr3ea_prod_qualitytourid || "").replace(/[{}]/g, "").trim().toLowerCase();
                    return id1 === cleanTourId || id2 === cleanTourId;
                });

                const targetTourId = tour?.cr3ea_prod_rajpura_quality_tourid || tour?.cr3ea_prod_qualitytourid || tourIdParam;
                dispatch({ type: 'SET_TOUR_ID', payload: targetTourId });

                // Check tour creation date for auto-expiration (previous day tours not closed/completed)
                const creationTime = tour?.createdon || tour?.cr3ea_tourstartdate;
                let currentStatus = String(tour?.cr3ea_status || tour?.cr3ea_processstatus || "In Progress").trim();

                if (creationTime) {
                    const parsedDate = moment(creationTime);
                    if (parsedDate.isValid()) {
                        const tourDateLocal = parsedDate.local().format("YYYY-MM-DD");
                        const todayLocal = moment().format("YYYY-MM-DD");
                        if (tourDateLocal !== todayLocal && 
                            currentStatus !== "Closed - Expired" && 
                            currentStatus !== "Completed" && 
                            currentStatus !== "Closed" && 
                            currentStatus !== "Success") {
                            
                            console.log(`Tour is from a previous day (${tourDateLocal}) and is in state "${currentStatus}". Auto-expiring and closing...`);
                            try {
                                await ccpOprpSievesService.saveSession({
                                    cr3ea_prod_rajpura_quality_tourid: targetTourId,
                                    cr3ea_status: "Closed - Expired",
                                    cr3ea_processstatus: "Closed - Expired"
                                });
                                currentStatus = "Closed - Expired";
                            } catch (e) {
                                console.error("Failed to automatically close/expire previous day's tour:", e);
                            }
                        }
                    }
                }

                // Detect parameter verification type
                let paramType: "CCP & OPRP" | "Sieves and Magnets" = "CCP & OPRP";
                if (tour?.cr3ea_ccp_oprp_sieves_parametertype) {
                    paramType = tour.cr3ea_ccp_oprp_sieves_parametertype.includes("Sieve") ? "Sieves and Magnets" : "CCP & OPRP";
                } else if (tour?.cr3ea_title) {
                    const title = String(tour.cr3ea_title);
                    if (title.toLowerCase().includes("sieve")) paramType = "Sieves and Magnets";
                }

                const frequency: "4hrs" | "8hrs" = (tour?.cr3ea_ccp_oprp_sieves_frequency === "4hrs") ? "4hrs" : "8hrs";
                const line = tour?.cr3ea_lineno || "Line-1";

                dispatch({
                    type: 'SET_SELECTIONS',
                    payload: {
                        parameterVerification: paramType,
                        shift: tour?.cr3ea_shift || "Shift 1",
                        frequency
                    }
                });

                dispatch({
                    type: 'SET_SESSION_DATA',
                    payload: {
                        site: tour?.cr3ea_plantid || "Rajpura",
                        line,
                        shift: tour?.cr3ea_shift || "Shift 1",
                        qaExecutive: tour?.cr3ea_assigned_qa || "",
                        productionIncharge: tour?.cr3ea_shiftexecutiveproduction || "",
                        productVariety: tour?.cr3ea_ccp_oprp_sieves_productvariety || "Cream Bourbon",
                        parameterVerification: paramType,
                        frequency,
                        cycle: "Cycle-1",
                        tourStartDate: tour?.cr3ea_tourstartdate || new Date().toISOString()
                    }
                });

                // Load existing completed cycles
                if (paramType === "CCP & OPRP") {
                    const checkpoints = await ccpOprpSievesService.getCCPOPRPCheckpoints(targetTourId);
                    if (checkpoints && checkpoints.length > 0) {
                        const cycleGroups: Record<string, any[]> = {};
                        checkpoints.forEach((cp: any) => {
                            const cName = cp.cr3ea_cycle || "Cycle-1";
                            if (!cycleGroups[cName]) cycleGroups[cName] = [];
                            cycleGroups[cName].push(cp);
                        });

                        const sortedCycleKeys = Object.keys(cycleGroups).sort((a, b) => {
                            const numA = parseInt(a.replace(/\D/g, ""), 10) || 0;
                            const numB = parseInt(b.replace(/\D/g, ""), 10) || 0;
                            return numA - numB;
                        });

                        const completedRecords: CompletedCycleRecord[] = sortedCycleKeys.map(cName => {
                            const rows = cycleGroups[cName];
                            const first = rows[0];
                            const formattedRows = rows.map((r: any) => ({
                                checkpointName: r.cr3ea_checkpointname || "",
                                description: r.cr3ea_checkpointdescription || "",
                                category: r.cr3ea_category || "OPRP",
                                criteria: r.cr3ea_criteria || "Okay",
                                mdSensitivityValue: r.cr3ea_mdsensitivityvalue,
                                defectRemarks: r.cr3ea_defectremarks,
                                actionTaken: r.cr3ea_actiontaken,
                                notifiedDepartment: r.cr3ea_notifieddepartment,
                                deviationStatus: r.cr3ea_deviationstatus,
                                escalated: r.cr3ea_escalated
                            }));

                            const total = formattedRows.length;
                            const ok = formattedRows.filter((f: any) => f.criteria === "Okay").length;
                            const score = total > 0 ? Math.round((ok / total) * 100) : 100;

                            return {
                                cycle: cName,
                                cycleDate: first?.cr3ea_tourstartdate || new Date().toISOString(),
                                acceptanceResponse: first?.cr3ea_acceptanceresponse || "OK",
                                acceptanceReason: first?.cr3ea_acceptanceresponse !== "OK" ? first?.cr3ea_defectremarks : undefined,
                                checkpoints: formattedRows,
                                score,
                                hasDeviations: formattedRows.some((f: any) => f.criteria === "Not Okay"),
                                status: "Submitted"
                            };
                        });

                        dispatch({ type: 'SET_COMPLETED_CYCLES', payload: completedRecords });
                        const maxCycleNum = sortedCycleKeys.reduce((max, k) => {
                            const num = parseInt(k.replace(/\D/g, ""), 10) || 0;
                            return num > max ? num : max;
                        }, 0);
                        const nextCycleNumber = maxCycleNum + 1;
                        dispatch({ type: 'SET_CURRENT_CYCLE', payload: `Cycle-${nextCycleNumber}` });
                    }
                } else {
                    const sievesItems = await ccpOprpSievesService.getSievesMagnetsCheckpoints(targetTourId);
                    if (sievesItems && sievesItems.length > 0) {
                        const cycleGroups: Record<string, any[]> = {};
                        sievesItems.forEach((si: any) => {
                            const cName = si.cr3ea_cycle || "Cycle-1";
                            if (!cycleGroups[cName]) cycleGroups[cName] = [];
                            cycleGroups[cName].push(si);
                        });

                        const sortedCycleKeys = Object.keys(cycleGroups).sort((a, b) => {
                            const numA = parseInt(a.replace(/\D/g, ""), 10) || 0;
                            const numB = parseInt(b.replace(/\D/g, ""), 10) || 0;
                            return numA - numB;
                        });

                        const completedRecords: CompletedCycleRecord[] = sortedCycleKeys.map(cName => {
                            const rows = cycleGroups[cName];
                            const first = rows[0];
                            const formattedRows = rows.map((r: any, idx: number) => {
                                const desc = r.cr3ea_description || "";
                                const isNewPlant = desc.includes("Line 5") || desc.includes("L-5") || desc.includes("Line 6") || desc.includes("L-6") || desc.includes("L-5&7");
                                const plantGroup: "New Plant" | "Old Plant" = isNewPlant ? "New Plant" : "Old Plant";

                                return {
                                    id: `resumed_${idx}`,
                                    description: desc,
                                    frequency: (r.cr3ea_frequency === "4hrs" ? "4hrs" : "8hrs") as "4hrs" | "8hrs",
                                    plantGroup,
                                    criteria: r.cr3ea_criteria || "Okay",
                                    defectRemarks: r.cr3ea_defectremarks
                                };
                            });

                            const total = formattedRows.length;
                            const ok = formattedRows.filter((f: any) => f.criteria === "Okay").length;
                            const score = total > 0 ? Math.round((ok / total) * 100) : 100;

                            return {
                                cycle: cName,
                                cycleDate: first?.createdon || new Date().toISOString(),
                                sievesItems: formattedRows,
                                score,
                                hasDeviations: formattedRows.some((f: any) => f.criteria === "Not Okay"),
                                status: "Submitted"
                            };
                        });

                        dispatch({ type: 'SET_COMPLETED_CYCLES', payload: completedRecords });
                        const maxCycleNum = sortedCycleKeys.reduce((max, k) => {
                            const num = parseInt(k.replace(/\D/g, ""), 10) || 0;
                            return num > max ? num : max;
                        }, 0);
                        const nextCycleNumber = maxCycleNum + 1;
                        dispatch({ type: 'SET_CURRENT_CYCLE', payload: `Cycle-${nextCycleNumber}` });
                    }
                }

                const isTerminal = currentStatus === "Submitted" || currentStatus === "Completed" || currentStatus === "Closed" || currentStatus === "Closed - Expired" || currentStatus === "Success";

                if (isTerminal) {
                    dispatch({ type: 'SET_SCREEN', payload: CCP_SCREENS.SUMMARY });
                } else {
                    if (paramType === "CCP & OPRP") {
                        dispatch({ type: 'SET_SCREEN', payload: CCP_SCREENS.CCP_OPRP_CHECKLIST });
                    } else {
                        dispatch({ type: 'SET_SCREEN', payload: CCP_SCREENS.SIEVES_MAGNETS_CHECKLIST });
                    }
                }
            } catch (err) {
                console.error("Error resuming CCP/OPRP/Sieves tour:", err);
            } finally {
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        };

        resumeTour();
    }, [tourIdParam]);

    const renderScreen = () => {
        if (state.isLoading) {
            return (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-4 text-sm font-semibold text-slate-600 dark:text-slate-400">Loading Quality Monitoring Session...</p>
                </div>
            );
        }

        switch (state.currentScreen) {
            case CCP_SCREENS.CHECKLIST_INFO:
                return <ChecklistInformation />;
            case CCP_SCREENS.CCP_OPRP_CHECKLIST:
                return <CCPOPRPChecklist />;
            case CCP_SCREENS.SIEVES_MAGNETS_CHECKLIST:
                return <SievesMagnetsChecklist />;
            case CCP_SCREENS.SUMMARY:
                return <SummaryReporting />;
            default:
                return <ChecklistInformation />;
        }
    };

    return (
        <DashboardLayout>
            <FullPageLoader isLoading={state.isLoading} message="Processing CCP / OPRP / Sieves request... Please wait." />
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 pb-12">
                {renderScreen()}
            </div>
        </DashboardLayout>
    );
};

const CCP_OPRP_Sieves: React.FC = () => {
    return (
        <CCPOPRPSievesProvider>
            <CCP_OPRP_SievesMain />
        </CCPOPRPSievesProvider>
    );
};

export default CCP_OPRP_Sieves;
